import { Alert, AlertIcon, Box, CloseButton } from "@chakra-ui/react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { FoaPart, ProcedureType } from "../../../../../autogen/bff-api";

interface Props {
  procedureType: ProcedureType | null | undefined;
  foaPart: FoaPart | null | undefined;
}

interface ProcedureChange {
  from: ProcedureType;
  to: ProcedureType;
}

export const ProcedureChangedNotice = ({ procedureType, foaPart }: Props) => {
  const { t } = useTranslation();
  const [change, dismiss] = useServerProcedureChange(procedureType ?? null);

  if (!change) return null;
  return (
    <Alert status="info" mt="3" borderRadius="md" fontSize="sm">
      <AlertIcon />
      <Box flex="1">
        {foaPart
          ? t("The procedure changed from {{from}} to {{to}} because the procurement is now {{part}}", {
              from: t(change.from),
              to: t(change.to),
              part: t(foaPart),
            })
          : t("The procedure changed from {{from}} to {{to}}", { from: t(change.from), to: t(change.to) })}
      </Box>
      <CloseButton size="md" aria-label={t("Close") ?? undefined} onClick={dismiss} />
    </Alert>
  );
};

const useServerProcedureChange = (procedureType: ProcedureType | null): [ProcedureChange | null, () => void] => {
  const [change, setChange] = useState<ProcedureChange | null>(null);
  const previous = useRef(procedureType);

  useEffect(() => {
    const before = previous.current;
    previous.current = procedureType;
    if (before == null || procedureType == null || before === procedureType) return;
    setChange((shown) => {
      const from = shown?.from ?? before;
      return from === procedureType ? null : { from, to: procedureType };
    });
  }, [procedureType]);

  return [change, () => setChange(null)];
};
